import React, { useEffect, useMemo, useRef } from "react";
import { Animated } from "react-native";
import Svg, { Line } from "react-native-svg";

import { useTheme } from "../ThemeProvider";

const AnimatedLine = Animated.createAnimatedComponent(Line);

export interface SelectionIconProps {
  checked: boolean;
  strokeColor?: string;
  testID?: string;
  size?: number;
  strokeWidth?: number;
}

/**
 * A selection icon that is used to indicate that a chip is selected.
 * This will animate between a plus icon and a check icon.
 * @param checked - Whether the chip is checked.
 * @param strokeColor - The color of the stroke.
 * @param testID - Test ID for testing purposes
 * @param size - The size of the icon.
 * @param strokeWidth - The width of the stroke.
 */
export function SelectionIcon({
  checked,
  strokeColor,
  testID,
  size = 20,
  strokeWidth = 2,
}: SelectionIconProps) {
  const animProgress = useRef(new Animated.Value(checked ? 1 : 0)).current;
  const theme = useTheme();
  const resolvedStrokeColor = useMemo(
    () => strokeColor ?? theme.colors.foregroundStrong,
    [strokeColor, theme],
  );

  useEffect(() => {
    const animation = Animated.timing(animProgress, {
      toValue: checked ? 1 : 0,
      duration: 200,
      useNativeDriver: false,
    });

    animation.start();

    return () => {
      animation.stop();
    };
  }, [checked, animProgress]);

  const width = size;

  // Account for stroke width to prevent off-center rendering
  const inset = strokeWidth / 2;
  const min = inset;
  const max = width - inset;
  const span = max - min;
  const toPx = (percentage: number) => min + percentage * span;

  // Plus icon coordinates
  const plusVerticalLineStart = { x: toPx(0.5), y: toPx(0.2) };
  const plusVerticalLineEnd = { x: toPx(0.5), y: toPx(0.8) };
  const plusHorizontalLineStart = { x: toPx(0.8), y: toPx(0.5) };
  const plusHorizontalLineEnd = { x: toPx(0.2), y: toPx(0.5) };

  // Check icon coordinates
  const checkLongLineStart = { x: toPx(0.8), y: toPx(0.3) };
  const checkLongLineEnd = { x: toPx(0.4), y: toPx(0.7) };
  const checkShortLineStart = checkLongLineEnd;
  const checkShortLineEnd = { x: toPx(0.2), y: toPx(0.5) };

  // Animated line positions
  const animatedX1A = animProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [plusVerticalLineStart.x, checkLongLineStart.x],
  });
  const animatedY1A = animProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [plusVerticalLineStart.y, checkLongLineStart.y],
  });
  const animatedX2A = animProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [plusVerticalLineEnd.x, checkLongLineEnd.x],
  });
  const animatedY2A = animProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [plusVerticalLineEnd.y, checkLongLineEnd.y],
  });

  const animatedX1B = animProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [plusHorizontalLineStart.x, checkShortLineStart.x],
  });
  const animatedY1B = animProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [plusHorizontalLineStart.y, checkShortLineStart.y],
  });
  const animatedX2B = animProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [plusHorizontalLineEnd.x, checkShortLineEnd.x],
  });
  const animatedY2B = animProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [plusHorizontalLineEnd.y, checkShortLineEnd.y],
  });

  return (
    <Svg
      height={width}
      width={width}
      viewBox={`0 0 ${width} ${width}`}
      preserveAspectRatio="xMidYMid meet"
      testID={`${testID}_${checked ? "checked" : "unchecked"}`}
      aria-hidden={true}
      accessible={false}
    >
      <AnimatedLine
        x1={animatedX1A}
        y1={animatedY1A}
        x2={animatedX2A}
        y2={animatedY2A}
        stroke={resolvedStrokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <AnimatedLine
        x1={animatedX1B}
        y1={animatedY1B}
        x2={animatedX2B}
        y2={animatedY2B}
        stroke={resolvedStrokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </Svg>
  );
}
